import React, { useCallback, useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View, useWindowDimensions } from "react-native";
import {
  ArrowSquareOutIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ChatTeardropIcon,
  CheckIcon,
  ClipboardTextIcon,
  DownloadSimpleIcon,
  FileIcon,
  FileTextIcon,
  InfoIcon,
  LinkIcon,
  ListChecksIcon,
  PencilSimpleIcon,
  ScalesIcon,
  ShieldIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";
import type { ActivityDetail, SegmentVariant, StructuredFields } from "../types/reviewTypes";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";
import ActivityPanelTabs from "./ActivityPanelTabs";
import IssueFindingsList from "./IssueFindingsList";
import SegmentedDiffView from "./SegmentedDiffView";

interface AssignmentPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFields) => void;
}

// ---------------------------------------------------------------------------
// Activity type presentation
// ---------------------------------------------------------------------------

function getActivityTypePresentation(activityType: string | undefined) {
  switch (activityType) {
    case "quiz":
      return { label: "Quiz", icon: ClipboardTextIcon, bgColor: "#EFF6FF", textColor: "#2563EB" };
    case "discussion":
      return { label: "Discussion", icon: ChatTeardropIcon, bgColor: "#F0FDF4", textColor: "#16A34A" };
    case "html-content":
      return { label: "HTML Content", icon: FileTextIcon, bgColor: "#FEF3C7", textColor: "#D97706" };
    case "webpage":
      return { label: "Webpage", icon: FileTextIcon, bgColor: "#FFF7ED", textColor: "#C2410C" };
    case "file":
      return { label: "File Topic", icon: FileTextIcon, bgColor: colors.grey[100], textColor: colors.grey[700] };
    case "link":
    case "externalLink":
      return { label: "External Link", icon: LinkIcon, bgColor: colors.grey[100], textColor: colors.grey[700] };
    case "checklist":
      return { label: "Checklist", icon: ListChecksIcon, bgColor: colors.grey[100], textColor: colors.grey[700] };
    default:
      return { label: "Assignment", icon: FileTextIcon, bgColor: colors.brand[50], textColor: colors.brand[700] };
  }
}

function getQualityPresentation(qualityLabel: string | undefined) {
  switch (qualityLabel?.toLowerCase()) {
    case "excellent":
      return { bgColor: "#E3FBCC", textColor: "#2B5314" };
    case "good":
      return { bgColor: "#CCD7FF", textColor: "#000B33" };
    case "fair":
      return { bgColor: "#FEF3C7", textColor: "#D97706" };
    case "poor":
    case "needs_revision":
      return { bgColor: colors.error[50], textColor: colors.error[700] };
    default:
      return null;
  }
}

function getContentCategoryPresentation(category: string | undefined) {
  switch (category) {
    case "lecture":
      return { label: "Lecture", bgColor: "#F5F0FE", textColor: "#7244BB" };
    case "supplementary":
      return { label: "Supplementary", bgColor: "#F2F4F7", textColor: "#344054" };
    case "instructional":
    case "instructional-content":
      return { label: "Instructional", bgColor: "#E6F4FF", textColor: "#2563EB" };
    default:
      return null;
  }
}

function getSegmentVariant(activityType: string | undefined): SegmentVariant {
  switch (activityType) {
    case "html-content":
      return "page";
    case "webpage":
      return "webpage";
    case "file":
      return "page";
    case "link":
    case "externalLink":
      return "link";
    case "checklist":
      return "checklist";
    default:
      return "activity";
  }
}

function getNoticeText(activityType: string | undefined): string | null {
  switch (activityType) {
    case "quiz":
      return "This shows a structural summary of the quiz. Individual question editing happens in your LMS.";
    case "discussion":
      return "This shows the discussion prompt and requirements. Forum settings are managed in your LMS.";
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Monolithic diff view (for link type — shows full text side by side)
// ---------------------------------------------------------------------------

interface MonolithicDiffViewProps {
  currentContent: string;
  proposedContent: string;
  isEditDisabled?: boolean;
  onSave?: (editedContent: string) => void;
}

function MonolithicDiffView({ currentContent, proposedContent, isEditDisabled, onSave }: MonolithicDiffViewProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const canEdit = Boolean(onSave) && !isEditDisabled;
  const [collapsed, setCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingText, setEditingText] = useState(proposedContent);
  const getRewriteProposedText = useCallback(() => editingText, [editingText]);
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    currentText: currentContent,
    getProposedText: getRewriteProposedText,
    onApplyRewrite: setEditingText,
  });

  useEffect(() => {
    setIsEditing(false);
    setCollapsed(false);
    setEditingText(proposedContent);
  }, [proposedContent]);

  useEffect(() => {
    setCollapsed(isEditing);
  }, [isEditing]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const handleEdit = useCallback(() => {
    if (!canEdit) return;
    setEditingText(proposedContent);
    setIsEditing(true);
  }, [canEdit, proposedContent]);

  const handleSave = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(false);
    onSave?.(editingText);
  }, [canEdit, editingText, onSave]);
  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditingText(proposedContent);
  }, [proposedContent]);

  return (
    <View style={monoStyles.container}>
      <View
        style={[
          monoStyles.headerRow,
          showTwoColumns && !collapsed ? monoStyles.headerRowDesktop : monoStyles.headerRowMobile,
        ]}>
        {!collapsed && (
          <View style={[monoStyles.currentHeader, showTwoColumns && monoStyles.headerHalf]}>
            <CustomText
              text="Current"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={monoStyles.currentHeaderText}
            />
            <Pressable onPress={() => setCollapsed(true)} style={monoStyles.iconButton}>
              <CaretLeftIcon size={14} color={colors.grey[500]} weight="bold" />
            </Pressable>
          </View>
        )}
        <View style={[monoStyles.proposedHeader, !collapsed && showTwoColumns && monoStyles.headerHalf]}>
          <View style={monoStyles.proposedHeaderLeft}>
            {collapsed && (
              <Pressable onPress={() => setCollapsed(false)} style={monoStyles.iconButton}>
                <CaretRightIcon size={14} color={colors.success[700]} weight="bold" />
              </Pressable>
            )}
            <CustomText
              text="Proposed"
              useTranslationText={false}
              size="s"
              weight="ultraBold"
              style={monoStyles.proposedHeaderText}
            />
          </View>
          {isEditing ? (
            <View style={monoStyles.editingActions}>
              <Pressable onPress={handleCancel} style={monoStyles.cancelBtn}>
                <XIcon size={12} color={colors.grey[600]} weight="bold" />
                <CustomText text="Cancel" useTranslationText={false} size="xs" style={monoStyles.cancelBtnText} />
              </Pressable>
              <AIRewriteActionButton disabled={isRewriting} onPress={openRewriteModal} />
              <Pressable onPress={handleSave} style={monoStyles.saveBtn}>
                <CheckIcon size={12} color={colors.white} weight="bold" />
                <CustomText text="Save" useTranslationText={false} size="xs" style={monoStyles.saveBtnText} />
              </Pressable>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Edit proposed changes"
              accessibilityState={{ disabled: !canEdit }}
              disabled={!canEdit}
              onPress={handleEdit}
              style={[monoStyles.editBtn, !canEdit && monoStyles.editBtnDisabled]}>
              <PencilSimpleIcon size={14} color={canEdit ? colors.grey[700] : colors.grey[400]} weight="regular" />
              <CustomText
                text="Edit"
                useTranslationText={false}
                size="xs"
                style={[monoStyles.editBtnText, !canEdit && monoStyles.editBtnTextDisabled]}
              />
            </Pressable>
          )}
        </View>
      </View>
      <View
        style={[
          monoStyles.bodyRow,
          showTwoColumns && !collapsed ? monoStyles.bodyRowDesktop : monoStyles.bodyRowMobile,
        ]}>
        {!collapsed && (
          <View
            style={[
              monoStyles.bodyCell,
              showTwoColumns && monoStyles.bodyCellHalf,
              showTwoColumns && monoStyles.bodyCellBorder,
            ]}>
            <CustomText text={currentContent} useTranslationText={false} size="s" style={monoStyles.bodyText} />
          </View>
        )}
        <View style={[monoStyles.bodyCell, !collapsed && showTwoColumns && monoStyles.bodyCellHalf]}>
          {isEditing ? (
            <TextInput
              value={editingText}
              onChangeText={setEditingText}
              multiline
              style={monoStyles.textarea}
              textAlignVertical="top"
            />
          ) : (
            <CustomText text={proposedContent} useTranslationText={false} size="s" style={monoStyles.bodyText} />
          )}
        </View>
      </View>
      <AIRewriteModal
        errorMessage={rewriteErrorMessage}
        isRewriting={isRewriting}
        onCancel={closeRewriteModal}
        onRewrite={submitRewrite}
        visible={isRewriteModalVisible}
      />
    </View>
  );
}

const monoStyles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  headerRow: {},
  headerRowDesktop: { flexDirection: "row", borderBottomWidth: 1, borderBottomColor: colors.grey[200] },
  headerRowMobile: { flexDirection: "column", borderBottomWidth: 1, borderBottomColor: colors.grey[200] },
  headerHalf: { flex: 1 },
  currentHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  currentHeaderText: { color: colors.grey[700] },
  proposedHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
  },
  proposedHeaderLeft: { flexDirection: "row", alignItems: "center", gap: 8 },
  proposedHeaderText: { color: colors.success[700] },
  iconButton: { padding: 4, borderRadius: 4 },
  editBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editBtnText: { color: colors.grey[700] },
  editBtnDisabled: {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    opacity: 0.7,
  },
  editBtnTextDisabled: { color: colors.grey[400] },
  editingActions: { flexDirection: "row", alignItems: "center", gap: 6 },
  cancelBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelBtnText: { color: colors.grey[600] },
  saveBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  saveBtnText: { color: colors.white },
  bodyRow: {},
  bodyRowDesktop: { flexDirection: "row" },
  bodyRowMobile: { flexDirection: "column" },
  bodyCell: { flex: 1, padding: 24 },
  bodyCellHalf: { flex: 1 },
  bodyCellBorder: { borderRightWidth: 1, borderRightColor: colors.grey[200] },
  bodyText: { color: colors.grey[700], lineHeight: 22 },
  textarea: {
    minHeight: 200,
    padding: 12,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AssignmentPanel({ selectedReviewItem, activityDetail, isEditDisabled, onSaveEdit }: AssignmentPanelProps) {
  const detail = activityDetail;
  const activityType = detail?.activityType;
  const typePresentation = getActivityTypePresentation(activityType);
  const qualityPresentation = detail?.qualityLabel ? getQualityPresentation(detail.qualityLabel) : null;
  const TypeIcon = typePresentation.icon;
  const categoryPresentation = getContentCategoryPresentation(detail?.contentCategory);
  const segmentVariant = getSegmentVariant(activityType);
  const noticeText = getNoticeText(activityType);
  const isFileType = activityType === "file";
  const isLinkType = activityType === "link" || activityType === "externalLink";
  const hasIssueFindings = Boolean(detail?.issueFindings?.length);

  const currentFields: StructuredFields = {
    title: detail?.currentTitle ?? selectedReviewItem.title,
    description: detail?.currentDescription ?? "",
    dueDate: detail?.dueDate,
    points: detail?.pointsPossible,
    url: detail?.url,
  };

  const proposedFields: StructuredFields = {
    title: detail?.recommendedTitle ?? selectedReviewItem.title,
    description: detail?.recommendedDescription ?? "",
    // cleared = reviewer explicitly removed the date → show "Not set"
    // recommendedDueDate set = reviewer changed it → show new date
    // neither touched = fall back to the synced date
    dueDate: detail?.recommendedDueDateCleared ? undefined : (detail?.recommendedDueDate ?? detail?.dueDate),
    points: detail?.recommendedPointsPossible ?? detail?.pointsPossible,
    url: detail?.url,
  };

  return (
    <View style={styles.panel}>
      {/* Badges row */}
      <View style={styles.badgesRow}>
        <View style={[styles.typeBadge, { backgroundColor: typePresentation.bgColor }]}>
          <TypeIcon size={14} color={typePresentation.textColor} weight="bold" />
          <CustomText
            text={typePresentation.label}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={{ color: typePresentation.textColor }}
          />
        </View>
        {qualityPresentation && (
          <View style={[styles.qualityBadge, { backgroundColor: qualityPresentation.bgColor }]}>
            <CustomText
              text={detail?.qualityLabel ?? ""}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={{ color: qualityPresentation.textColor }}
            />
          </View>
        )}
        {categoryPresentation && (
          <View style={[styles.categoryBadge, { backgroundColor: categoryPresentation.bgColor }]}>
            <CustomText
              text={categoryPresentation.label}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={{ color: categoryPresentation.textColor }}
            />
          </View>
        )}
      </View>

      {/* Title + Metadata (flush, no gap) */}
      <View>
        <CustomText
          text={selectedReviewItem.title}
          useTranslationText={false}
          size="xl"
          weight="bold"
          style={styles.title}
        />
        <View style={styles.metaRow}>
          {detail?.pointsPossible != null && (
            <View style={styles.metaItem}>
              <ScalesIcon size={14} color={colors.grey[500]} weight="regular" />
              <CustomText
                text={`${detail.pointsPossible} pts`}
                useTranslationText={false}
                size="xs"
                style={styles.metaText}
              />
            </View>
          )}
          {detail?.documentReference && detail?.qualityLabel?.toLowerCase() !== "excellent" && (
            <View style={styles.metaItem}>
              <FileTextIcon size={14} color={colors.grey[500]} weight="regular" />
              <CustomText
                text={detail.documentReference}
                useTranslationText={false}
                size="xs"
                style={styles.metaText}
              />
            </View>
          )}
        </View>
      </View>

      <ActivityPanelTabs attachments={detail?.attachments}>
        <View style={styles.reviewContent}>
          {/* Why card */}
          {hasIssueFindings && (
            <View style={styles.whyCard}>
              <CustomText
                text="Why we're recommending changes"
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.whyHeading}
              />
              <IssueFindingsList findings={detail?.issueFindingsDetailed} />
              {detail?.rubricCode && (
                <View style={styles.rubricRow}>
                  <ShieldIcon size={12} color={colors.brand[600]} weight="fill" />
                  <CustomText
                    text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                    useTranslationText={false}
                    size="xs"
                    weight="medium"
                    style={styles.rubricLink}
                  />
                  {detail.rubricDescription && (
                    <CustomText
                      text={` \u2014 ${detail.rubricDescription}`}
                      useTranslationText={false}
                      size="xs"
                      style={styles.rubricDescriptionText}
                    />
                  )}
                </View>
              )}

              {/* Document Feedback — inside the Why card for file types */}
              {isFileType && detail?.documentFeedbackNote && (
                <>
                  <View style={styles.whyDivider} />
                  <CustomText
                    text="Document Feedback"
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.docFeedbackHeading}
                  />
                  <CustomText
                    text={`${detail.documentFeedbackLabel} \u2014 ${detail.documentFeedbackNote}`}
                    useTranslationText={false}
                    size="xs"
                    style={styles.docFeedbackNote}
                  />
                  {detail.documentFeedbackAction && (
                    <View style={styles.docFeedbackActionButton}>
                      <DownloadSimpleIcon size={14} color={colors.grey[700]} weight="bold" />
                      <CustomText
                        text={detail.documentFeedbackAction}
                        useTranslationText={false}
                        size="xs"
                        weight="medium"
                        style={styles.docFeedbackActionText}
                      />
                    </View>
                  )}
                </>
              )}
            </View>
          )}

          {/* Type-specific notice */}
          {noticeText && (
            <View style={styles.noticeBar}>
              <InfoIcon size={14} color="#3B82F6" weight="fill" />
              <CustomText text={noticeText} useTranslationText={false} size="xs" style={styles.noticeText} />
            </View>
          )}

          {isFileType && detail?.fileName && (
            <View style={styles.fileDetailsCard}>
              <CustomText
                text="File Details"
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.fileDetailsLabel}
              />
              <View style={styles.fileDetailsRow}>
                <FileIcon size={16} color={colors.grey[500]} weight="regular" />
                <View>
                  <CustomText
                    text={`File: ${detail.fileName}`}
                    useTranslationText={false}
                    size="s"
                    style={styles.fileDetailsText}
                  />
                  {detail.fileLocation && (
                    <CustomText
                      text={`Location: ${detail.fileLocation}`}
                      useTranslationText={false}
                      size="xs"
                      style={styles.fileDetailsLocation}
                    />
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Link type: Current URL card + monolithic diff */}
          {isLinkType && detail?.url && (
            <View style={styles.currentUrlCard}>
              <CustomText
                text="CURRENT URL"
                useTranslationText={false}
                size="xs"
                weight="bold"
                style={styles.currentUrlLabel}
              />
              <View style={styles.currentUrlRow}>
                <ArrowSquareOutIcon size={14} color={colors.brand[600]} weight="regular" />
                <CustomText
                  text={detail.url}
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  style={styles.currentUrlText}
                  numberOfLines={1}
                />
              </View>
            </View>
          )}

          {isLinkType && detail?.currentDescription && detail?.recommendedDescription && (
            <MonolithicDiffView
              currentContent={detail.currentDescription}
              proposedContent={detail.recommendedDescription}
              isEditDisabled={isEditDisabled}
              onSave={(description) => onSaveEdit?.({ ...proposedFields, description })}
            />
          )}

          {/* Segmented diff view — for assignment, discussion, html-content, checklist, quiz types */}
          {!isFileType && !isLinkType && (
            <SegmentedDiffView
              variant={segmentVariant}
              currentFields={currentFields}
              proposedFields={proposedFields}
              isEditDisabled={isEditDisabled}
              onSaveEdit={onSaveEdit}
            />
          )}

          {/* Last modified */}
          {detail?.lastModifiedBy && (
            <CustomText
              text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
              useTranslationText={false}
              size="xs"
              style={styles.lastModified}
            />
          )}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  reviewContent: {
    gap: 16,
  },
  badgesRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    height: 24,
    borderRadius: 8,
  },
  qualityBadge: {
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 9999,
    height: 24,
    justifyContent: "center",
  },
  categoryBadge: {
    paddingHorizontal: 8,
    height: 24,
    justifyContent: "center",
    borderRadius: 8,
  },
  title: {
    color: colors.grey[900],
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    color: colors.grey[500],
  },
  // --- Why card ---
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 2,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescriptionText: {
    color: colors.grey[500],
  },
  // --- Notice bar ---
  noticeBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 16,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  noticeText: {
    color: colors.grey[600],
    lineHeight: 18,
    flex: 1,
  },
  // --- Current URL (link type) ---
  currentUrlCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 8,
  },
  currentUrlLabel: {
    color: colors.grey[400],
    fontSize: 11,
    letterSpacing: 0.5,
  },
  currentUrlRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  currentUrlText: {
    color: colors.brand[600],
    flex: 1,
    fontFamily: "monospace",
  },
  whyDivider: {
    height: 1,
    backgroundColor: colors.grey[100],
    marginVertical: 4,
  },
  // --- Document Feedback (file type, rendered inside Why card) ---
  docFeedbackHeading: {
    color: colors.grey[700],
  },
  docFeedbackNote: {
    color: colors.grey[500],
    lineHeight: 18,
  },
  docFeedbackActionButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.white,
    marginTop: 4,
  },
  docFeedbackActionText: {
    color: colors.grey[700],
  },
  // --- File Details (file type) ---
  fileDetailsCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 10,
  },
  fileDetailsLabel: {
    color: colors.grey[500],
    fontSize: 11,
    letterSpacing: 0.275,
    textTransform: "uppercase" as const,
  },
  fileDetailsRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },
  fileDetailsText: {
    color: colors.grey[700],
  },
  fileDetailsLocation: {
    color: colors.grey[500],
    marginTop: 2,
  },
  lastModified: {
    color: colors.grey[400],
  },
});

export default AssignmentPanel;
